import { useRef, useState } from "react";
import { Tooltip } from "@/components/ui/tooltip";
import { t } from "@/i18n/id";
import { cn } from "@/lib/cn";

/**
 * Drag edge on the inspector's left border (desktop). Dragging left widens the panel. The width is
 * reported live through `onResize`, and once more through `onCommit` when the drag ends (to store it).
 * Keyboard: ←/→ widen/narrow (Shift: bigger steps), Home/End for the narrowest/widest. Double-click or Enter: default width.
 */
export function ResizeHandle({
  width,
  min,
  max,
  onResize,
  onCommit,
  onReset,
}: {
  width: number;
  min: number;
  max: number;
  onResize: (width: number) => void;
  onCommit: (width: number) => void;
  onReset: () => void;
}) {
  const drag = useRef<{ x: number; width: number; last: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const clamp = (w: number) => Math.round(Math.min(max, Math.max(min, w)));
  const stop = () => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    document.body.classList.remove("select-none", "cursor-col-resize");
    if (d) onCommit(d.last);
  };
  return (
    <Tooltip content={t.editor.panel.resizeHint} side="left">
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t.editor.panel.resize}
        aria-valuenow={width}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={0}
        data-dragging={dragging ? "" : undefined}
        className={cn(
          "group absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none outline-none max-lg:hidden",
          "after:absolute after:inset-y-0 after:left-[3px] after:w-0.5 after:bg-primary after:opacity-0 after:transition-opacity hover:after:opacity-60 focus-visible:after:opacity-100 data-[dragging]:after:opacity-100",
        )}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, width, last: width };
          setDragging(true);
          document.body.classList.add("select-none", "cursor-col-resize");
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          d.last = clamp(d.width + (d.x - e.clientX));
          onResize(d.last);
        }}
        onPointerUp={stop}
        onPointerCancel={stop}
        onDoubleClick={onReset}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 96 : 24;
          const next = e.key === "ArrowLeft" ? width + step : e.key === "ArrowRight" ? width - step : e.key === "Home" ? min : e.key === "End" ? max : null;
          if (e.key === "Enter") {
            e.preventDefault();
            onReset();
            return;
          }
          if (next === null) return;
          e.preventDefault();
          onCommit(clamp(next));
        }}
      />
    </Tooltip>
  );
}
